import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './PageSuggestions.css';
import { useNavigate } from './Router';
import { Button, Alert } from 'reactstrap';
import { FaChevronLeft, FaChevronRight, FaThumbsUp } from 'react-icons/fa';

const PageSuggestions = ({onChange}) => {
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);

    const [pages, setPages] = useState([]);
    const [errors, setErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [shouldDisplay, setShouldDisplay] = useState(true);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    const goToPage = (pageId) => {
        navigate("/page/" + pageId);
    };

    const fetchSugg = async () => {
        setIsLoading(true);
        try {
            const ps = await sdk.models.SocialPages.suggestions({});
            if (!ps.error)
                setPages(ps || []);
            setErrors(null);
        } catch (err) {
            console.error("Erreur lors du chargement des suggestions:", err);
            setErrors("Une erreur s'est produite lors du chargement des suggestions.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLike = async (pageId, event) => {
        event.stopPropagation();
        try {
            await sdk.models.PageLike.create({
                page_id: pageId,
                time: Math.floor(Date.now() / 1000),
            });
            
            // Mise à jour de l'état du "like" pour cette page
            setPages((prevPages) =>
                prevPages.map((page) =>
                    page.page_id === pageId ? { ...page, liked: true } : page
                )
            );
            
            // Appeler le callback onChange si défini
            if (typeof onChange === "function") onChange();
        } catch (err) {
            console.error("Erreur lors de l'action 'Aimer':", err);
            setErrors("Une erreur s'est produite lors de l'action 'Aimer'. Veuillez réessayer.");
        }
    };

    const scrollLeft = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const handleScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const checkDisplayFrequency = () => {
        return setShouldDisplay(true);
        
        const lastDisplay = localStorage.getItem("pageSuggestionsLastDisplay");
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();

        if (!lastDisplay || now - parseInt(lastDisplay, 10) > oneWeekInMs) {
            setShouldDisplay(true);
            localStorage.setItem("pageSuggestionsLastDisplay", now.toString());
        } else {
            setShouldDisplay(true); // Pour le développement, toujours afficher
        }
    };

    useEffect(() => {
        checkDisplayFrequency();
    }, []);

    useEffect(() => {
        if (shouldDisplay) {
            fetchSugg();
        }
    }, [shouldDisplay]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            // Vérifier initialement si on a besoin des flèches
            handleScroll();
        }
        
        return () => {
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, [pages]);

    if (!shouldDisplay || (!isLoading && pages.length === 0)) return null;

    return (
        <div className="page-suggestions-container">
            <div className="page-suggestions-header">
                <h2 className="page-suggestions-title">Suggestions de pages</h2>
                <Button color="link" onClick={fetchSugg} className="page-suggestions-refresh-btn">
                    Actualiser
                </Button>
            </div>
            
            {errors && (
                <Alert color="danger" className="error-message">
                    {errors}
                </Alert>
            )}
            
            {isLoading ? (
                <div className="page-suggestions-loading">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            ) : (
                <div className="page-suggestions-scroll-container-wrapper">
                    {showLeftArrow && (
                        <div 
                            className="page-suggestions-scroll-arrow page-suggestions-scroll-left"
                            onClick={scrollLeft}
                            aria-label="Défiler vers la gauche"
                        >
                            <FaChevronLeft />
                        </div>
                    )}
                    
                    <div 
                        className="page-suggestions-scroll-container" 
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                    >
                        {pages.map((page) => (
                            <div 
                                key={page.page_id || page.id} 
                                className="page-card"
                                onClick={() => goToPage(page.page_id)}
                            >
                                <div className="page-card-header">
                                    <img
                                        src={`https://peupleqc.com/s/peupleqc-upload/${page.avatar}`}
                                        alt={`${page.page_title} avatar`}
                                        className="page-avatar"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://peupleqc.com/s/peupleqc-upload/default-page.jpg";
                                        }}
                                    />
                                    <div className="page-info">
                                        <h3 className="page-title">{page.page_title}</h3>
                                        <p className="page-likes">{page.nbrLikes || 0} J'aime</p>
                                    </div>
                                </div>
                                
                                <div className="page-cover-wrapper">
                                    <img
                                        src={`https://peupleqc.com/s/peupleqc-upload/${page.cover}`}
                                        alt={`${page.page_title} cover`}
                                        className="page-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://peupleqc.com/s/peupleqc-upload/default-cover.jpg";
                                        }}
                                    />
                                </div>
                                
                                <div className="page-description">
                                    {page.description || "Découvrez cette page sur PeupleQc!"}
                                </div>
                                
                                <button
                                    className={`like-button ${page.liked ? "liked" : ""}`}
                                    onClick={(e) => handleLike(page.page_id, e)}
                                    disabled={page.liked}
                                >
                                    <FaThumbsUp /> {page.liked ? "Page aimée" : "J'aime"}
                                </button>
                            </div>
                        ))}
                    </div>
                    
                    {showRightArrow && (
                        <div 
                            className="page-suggestions-scroll-arrow page-suggestions-scroll-right"
                            onClick={scrollRight}
                            aria-label="Défiler vers la droite"
                        >
                            <FaChevronRight />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { PageSuggestions };